import React from "react";
import { useCallback, useEffect, useRef } from "react";
import Feature from "ol/Feature";
import { Coordinate } from "ol/coordinate";
import { Point } from "ol/geom";
import { fromLonLat } from "ol/proj";
import Icon from "ol/style/Icon";
import { Text } from "ol/style";
import Style from "ol/style/Style";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { useMap } from "../../hooks/incontext/useMap";
import { makeText } from "../../utils/object";
import { Annotation } from ".";
import { useInteractionEvent } from "../../hooks/incontext/useInteractionEvent";
import { Geometry } from "ol/geom";

export interface ImageMarkerProps extends Annotation {
  center: Coordinate;
  imageSrc: string;
  selected?: boolean;
}

export const ImageMarker = ({
  center,
  imageSrc,
  color = "BLUE",
  properties = {},
  onClick,
  onHover,
  onLeave,
  zIndex = 0,
  children,
  opacity = 1,
  isDisabledSelection = false,
  selected = false,
}: ImageMarkerProps) => {
  const map = useMap();
  const annotationRef = useRef<Feature<Point>>(
    new Feature(new Point(fromLonLat(center)))
  );

  const annotationLayerRef = useRef<
    VectorLayer<VectorSource<Feature<Geometry>>>
  >(
    new VectorLayer({
      source: new VectorSource({
        wrapX: false,
        features: [annotationRef.current],
      }),
    })
  );

  const annotationStyleRef = useRef(
    new Style({
      image: new Icon({
        opacity,
        src: imageSrc,
        scale: 0.17,
        anchor: [0.5, 1], // 마커 이미지의 앵커 위치
      }),
      text:
        children && !children.props.isPopup
          ? makeText({
              text: children.props.children || "",
              size: children.props.size || 15,
              color: children.props.color ? children.props.color : "black",
              outline: children.props.outline,
              isMarker: children.props.upperText,
            })
          : undefined,
    })
  );

  const onHoverHandler = useCallback(
    (feature: Feature<Geometry>) => {
      if (feature) {
        if (onHover) {
          onHover({ annotation: annotationRef.current, properties });
        }
      }

      // 수정중일땐 팝업 관여하지 않음
      if (map.getProperties().isModifying) return;

      // Pop up text
      if (feature && children?.props.isPopup) {
        const hoveredFeature = feature;
        const hoveredFeatureStyle = hoveredFeature.getStyle() as Style;
        hoveredFeatureStyle.setText(
          makeText({
            text: children.props.children || "",
            size: children.props.size || 15,
            color: children.props.color ? children.props.color : "black",
            outline: children.props.outline,
            isMarker: false,
          })
        );

        annotationRef.current.setStyle(hoveredFeatureStyle);
      } else if (feature && children?.props.isPopup) {
        const hoveredFeatureStyle = annotationRef.current.getStyle() as Style;

        hoveredFeatureStyle.setText(new Text());
        annotationRef.current.setStyle(hoveredFeatureStyle);
      }
    },
    [children, map, onHover, properties]
  );

  const onClickHandler = () => {
    if (onClick) {
      onClick({
        annotation: annotationRef.current,
        properties,
      });
    }
    // 선택된 Feature에 대한 작업 수행
    // 예: 스타일 변경, 정보 표시 등
  };

  useInteractionEvent({
    annotation: annotationLayerRef.current,
    onClick: onClickHandler,
    onHover: onHoverHandler,
    onLeave,
    isDisabledSelection,
  });

  useEffect(() => {
    if (annotationRef.current) {
      const geometry = annotationRef.current.getGeometry() as Point;
      geometry.setCoordinates(fromLonLat(center));
    }
  }, [center]);

  useEffect(() => {
    if (annotationLayerRef.current) {
      annotationLayerRef.current.setZIndex(zIndex);
    }
  }, [zIndex]);

  useEffect(() => {
    annotationStyleRef.current.setImage(
      new Icon({
        opacity,
        src: imageSrc,
        scale: 0.17,
        anchor: [0.5, 1], // 마커 이미지의 앵커 위치
      })
    );

    annotationRef.current.setStyle(annotationStyleRef.current);
  }, [imageSrc, opacity]);

  useEffect(() => {
    if (!children?.props?.color) return;
    const gottonText = annotationStyleRef.current.getText();
    if (gottonText && gottonText.getFill()) {
      gottonText.getFill()?.setColor(children.props.color);
    }

    annotationRef.current.setStyle(annotationStyleRef.current);
  }, [color, children]);

  useEffect(() => {
    if (annotationLayerRef.current && children && !children.props.isPopup) {
      annotationStyleRef.current.setText(
        makeText({
          text: children?.props?.children || "",
          size: children?.props?.size || 15,
          color: children?.props?.color ? children.props.color : "black",
          outline: children?.props?.outline,
          isMarker: true,
        })
      );
    }
  }, [children]);

  useEffect(() => {
    if (!children) {
      annotationStyleRef.current.setText(new Text());
    }
  }, [children]);

  useEffect(() => {
    annotationRef.current.setProperties({
      ...properties,
      shape: "ImageMarker",
      isModifying: false,
      source: annotationLayerRef.current.getSource(),
      layer: annotationLayerRef.current,
      hasPopup: children ? children?.props.isPopup : false,
    });
  }, [properties]);

  useEffect(() => {
    const annotationLayerCurrent = annotationLayerRef.current;
    map.addLayer(annotationLayerCurrent);
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      annotationLayerCurrent.getSource()?.clear();
      map.removeLayer(annotationLayerCurrent);
    };
  }, [map]);
  return null;
};
